import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Chip,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Avatar, InputAdornment, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Switch,
} from '@mui/material';
import {
  Add, Refresh, DirectionsBus, Person, Phone, Group, GpsFixed, GpsOff,
  GroupAdd, LocationOn, Search, CheckBox, CheckBoxOutlineBlank,
  Assessment, Timeline, CalendarMonth, Map as MapIcon, ArrowBack,
} from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import dayjs from 'dayjs';

const GPS_STALE_SEC = 90;

const getGpsStatus = (lat, lng, lastUpdate) => {
  if (!lat || !lng || !lastUpdate) return 'off';
  const normalized = String(lastUpdate).replace(' ', 'T');
  const diff = Math.floor((Date.now() - new Date(normalized).getTime()) / 1000);
  return diff <= GPS_STALE_SEC ? 'active' : 'off';
};

const STATUS_COLOR = {
  active:      { bg: '#e8f5e9', color: '#2e7d32', border: '#2e7d32' },
  inactive:    { bg: '#ffebee', color: '#c62828', border: '#c62828' },
  maintenance: { bg: '#fff3e0', color: '#e65100', border: '#e65100' },
};

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [addDialog, setAddDialog] = useState(false);
  const [form, setForm] = useState({ vehicle_number: '', vehicle_name: '', driver_name: '', driver_phone: '', driver_license: '', capacity: '30', gps_device_id: '' });
  const [saving, setSaving] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [studentsDialog, setStudentsDialog] = useState(false);
  const [allStudents, setAllStudents] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [trackDialog, setTrackDialog] = useState(false);
  const [location, setLocation] = useState(null);
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackInterval, setTrackInterval] = useState(null);

  // Summary state
  const [summaryDialog, setSummaryDialog] = useState(false);
  const [summaryVehicle, setSummaryVehicle] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryMonth, setSummaryMonth] = useState(dayjs().format('YYYY-MM'));
  const [summaryError, setSummaryError] = useState(null);

  // Track Path state
  const [pathDialog, setPathDialog] = useState(false);
  const [pathDate, setPathDate] = useState(null);
  const [pathPoints, setPathPoints] = useState([]);
  const [pathLoading, setPathLoading] = useState(false);
  const [pathError, setPathError] = useState(null);
  const [mapLayer, setMapLayer] = useState('street');
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const tileLayerRef = useRef(null);

  const MAP_LAYERS = {
    street:    { label: 'Street',    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',                                                                  attribution: '© OpenStreetMap contributors', maxZoom: 19 },
    satellite: { label: 'Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',                        attribution: '© Esri', maxZoom: 19 },
    terrain:   { label: 'Terrain',   url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',                                                                    attribution: '© OpenTopoMap contributors', maxZoom: 17 },
    dark:      { label: 'Dark',      url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',                                                       attribution: '© CartoDB', maxZoom: 19 },
  };

  const load = useCallback(async () => {
    setLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getVehicles, {});
    setVehicles(data?.vehicles || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (trackInterval) clearInterval(trackInterval); }, [trackInterval]);

  const handleAdd = async () => {
    if (!form.vehicle_number || !form.vehicle_name || !form.driver_name || !form.driver_phone) {
      alert('Vehicle number, name, driver name and phone are required'); return;
    }
    if (!/^\d{10}$/.test(form.driver_phone)) { alert('Driver phone must be 10 digits'); return; }
    setSaving(true);
    const data = await InvokeApi(Config.main_url + apiList.addVehicle, form);
    setSaving(false);
    if (data?.code === '200') {
      setAddDialog(false);
      setForm({ vehicle_number: '', vehicle_name: '', driver_name: '', driver_phone: '', driver_license: '', capacity: '30', gps_device_id: '' });
      load();
    } else alert(data?.message || 'Failed');
  };

  const openStudents = async (vehicle) => {
    setSelectedVehicle(vehicle);
    setStudentsDialog(true);
    setStudentsLoading(true);
    setStudentSearch('');
    const data = await InvokeApi(Config.main_url + apiList.getStudentsForVehicle, { vehicle_id: vehicle.id });
    const list = data?.students || [];
    setAllStudents(list);
    setSelectedIds(list.filter(s => parseInt(s.is_assigned) === 1).map(s => s.ID));
    setStudentsLoading(false);
  };

  const toggleStudent = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleAssign = async () => {
    if (selectedIds.length > parseInt(selectedVehicle.capacity || 999)) {
      alert('Cannot assign more than ' + selectedVehicle.capacity + ' students'); return;
    }
    setAssigning(true);
    const data = await InvokeApi(Config.main_url + apiList.assignStudentsToVehicle, {
      vehicle_id: selectedVehicle.id,
      student_ids: selectedIds,
    });
    setAssigning(false);
    if (data?.code === '200') { setStudentsDialog(false); load(); }
    else alert(data?.message || 'Failed');
  };

  const fetchLocation = async (vehicleId) => {
    setTrackLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getVehicleLocation, { vehicle_id: vehicleId });
    if (data?.code === '200' && data?.location) {
      setLocation(data.location);
    } else if (data?.code !== '200') {
      setLocation(null);
    }
    setTrackLoading(false);
  };

  const openTracking = (vehicle) => {
    setSelectedVehicle(vehicle);
    setLocation(null);
    setTrackDialog(true);
    fetchLocation(vehicle.id);
    const interval = setInterval(() => fetchLocation(vehicle.id), 10000);
    setTrackInterval(interval);
  };

  const closeTracking = () => {
    if (trackInterval) { clearInterval(trackInterval); setTrackInterval(null); }
    setTrackDialog(false);
    setLocation(null);
  };

  const openSummary = async (vehicle) => {
    setSummaryVehicle(vehicle);
    setSummaryData(null);
    setSummaryError(null);
    setSummaryDialog(true);
    await fetchSummary(vehicle.id, summaryMonth);
  };

  const fetchSummary = async (vehicleId, monthStr) => {
    setSummaryLoading(true);
    setSummaryError(null);
    const [year, month] = monthStr.split('-');
    try {
      const data = await InvokeApi(Config.main_url + apiList.getVehicleSummary, {
        vehicle_id: vehicleId,
        month: parseInt(month),
        year: parseInt(year),
      });
      console.log('[VehicleSummary] raw response:', JSON.stringify(data));
      if (data?.code === '200' && data?.summary) {
        setSummaryData(data.summary);
      } else {
        setSummaryData(null);
        setSummaryError(data?.message || `API returned code ${data?.code || 'unknown'}`);
      }
    } catch (err) {
      console.error('[VehicleSummary] error:', err);
      setSummaryData(null);
      setSummaryError('Network error: ' + err.message);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleSummaryMonthChange = async (newMonth) => {
    setSummaryMonth(newMonth);
    if (summaryVehicle) await fetchSummary(summaryVehicle.id, newMonth);
  };

  // ── Track Path ────────────────────────────────────────────────────────────
  const openPathDialog = async (day) => {
    setPathDate(day.date);
    setPathPoints([]);
    setPathError(null);
    setPathDialog(true);
    setPathLoading(true);
    try {
      const data = await InvokeApi(Config.main_url + apiList.getVehiclePathHistory, {
        vehicle_id: summaryVehicle.id,
        date: day.date,
      });
      if (data?.code === '200' && Array.isArray(data?.points) && data.points.length > 0) {
        setPathPoints(data.points);
      } else {
        setPathError(data?.message || 'No GPS path data available for this date.');
      }
    } catch (err) {
      setPathError('Failed to load path data: ' + err.message);
    } finally {
      setPathLoading(false);
    }
  };

  // Initialize Leaflet map once points are loaded
  useEffect(() => {
    if (!pathDialog || pathLoading || pathPoints.length === 0 || !mapRef.current) return;

    // Destroy previous map instance if any
    if (leafletMapRef.current) {
      leafletMapRef.current.remove();
      leafletMapRef.current = null;
      tileLayerRef.current = null;
    }

    const initMap = () => {
      const L = window.L;
      if (!L) return;

      const coords = pathPoints.map(p => [parseFloat(p.latitude), parseFloat(p.longitude)]);
      const map = L.map(mapRef.current).fitBounds(coords);
      leafletMapRef.current = map;

      // Tile layer — use current mapLayer selection
      const layer = MAP_LAYERS[mapLayer] || MAP_LAYERS.street;
      tileLayerRef.current = L.tileLayer(layer.url, {
        attribution: layer.attribution,
        maxZoom: layer.maxZoom,
      }).addTo(map);

      // Draw the path polyline
      L.polyline(coords, { color: '#1a237e', weight: 4, opacity: 0.85 }).addTo(map);

      // Start marker (green)
      const startIcon = L.divIcon({
        html: '<div style="background:#2e7d32;width:14px;height:14px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>',
        iconSize: [14, 14], iconAnchor: [7, 7], className: '',
      });
      L.marker(coords[0], { icon: startIcon })
        .addTo(map)
        .bindPopup(`<b>Start</b><br>${dayjs(String(pathPoints[0].timestamp).replace(' ', 'T')).format('hh:mm:ss A')}`)
        .openPopup();

      // End marker (red)
      const endIcon = L.divIcon({
        html: '<div style="background:#c62828;width:14px;height:14px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>',
        iconSize: [14, 14], iconAnchor: [7, 7], className: '',
      });
      const last = pathPoints[pathPoints.length - 1];
      L.marker(coords[coords.length - 1], { icon: endIcon })
        .addTo(map)
        .bindPopup(`<b>End</b><br>${dayjs(String(last.timestamp).replace(' ', 'T')).format('hh:mm:ss A')}`);
    };

    // Load Leaflet CSS + JS if not already loaded
    if (!window.L) {
      if (!document.getElementById('leaflet-css')) {
        const link = document.createElement('link');
        link.id = 'leaflet-css';
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }
      if (!document.getElementById('leaflet-js')) {
        const script = document.createElement('script');
        script.id = 'leaflet-js';
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = initMap;
        document.head.appendChild(script);
      }
    } else {
      initMap();
    }

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
        tileLayerRef.current = null;
      }
    };
  }, [pathDialog, pathLoading, pathPoints]);

  // Swap tile layer when mapLayer changes (without reinitializing the whole map)
  useEffect(() => {
    const L = window.L;
    if (!L || !leafletMapRef.current || !tileLayerRef.current) return;
    const layer = MAP_LAYERS[mapLayer] || MAP_LAYERS.street;
    leafletMapRef.current.removeLayer(tileLayerRef.current);
    tileLayerRef.current = L.tileLayer(layer.url, {
      attribution: layer.attribution,
      maxZoom: layer.maxZoom,
    }).addTo(leafletMapRef.current);
  }, [mapLayer]);

  const closePathDialog = () => {
    if (leafletMapRef.current) {
      leafletMapRef.current.remove();
      leafletMapRef.current = null;
      tileLayerRef.current = null;
    }
    setPathDialog(false);
    setPathPoints([]);
    setPathError(null);
    setPathDate(null);
    setMapLayer('street');
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const displayed = showInactive ? vehicles : vehicles.filter(v => (v.status || 'active') !== 'inactive');
  const filteredStudents = allStudents.filter(s =>
    !studentSearch || s.full_name?.toLowerCase().includes(studentSearch.toLowerCase()) ||
    s.standard?.toLowerCase().includes(studentSearch.toLowerCase())
  );
  const assignedStudents = filteredStudents.filter(s => parseInt(s.is_assigned) === 1);
  const unassignedStudents = filteredStudents.filter(s => parseInt(s.is_assigned) === 0);

  const activeCount = vehicles.filter(v => (v.status || 'active') === 'active').length;
  const inactiveCount = vehicles.filter(v => v.status === 'inactive').length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Vehicle Management</Typography>
          <Typography variant="body2" color="text.secondary">
            {activeCount} active · {inactiveCount} inactive
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid #e8eaf6', borderRadius: 2, px: 1.5, py: 0.5 }}>
            <Typography fontSize={12} fontWeight={600} color="text.secondary">Show Inactive</Typography>
            <Switch checked={showInactive} onChange={e => setShowInactive(e.target.checked)} size="small"
              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#1a237e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#1a237e' } }} />
          </Box> */}
          <Tooltip title="Refresh">
            <IconButton onClick={load} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}><Refresh /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => setAddDialog(true)}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            Add Vehicle
          </Button>
        </Box>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
        ) : displayed.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <DirectionsBus sx={{ fontSize: 64, color: '#e8eaf6', mb: 2 }} />
            <Typography color="text.secondary" fontWeight={500}>No vehicles registered yet</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f5f6fa' }}>
                  {['#', 'Vehicle', 'Driver', 'Capacity', 'GPS', 'Status', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayed.map((v, i) => {
                  const sc = STATUS_COLOR[v.status] || STATUS_COLOR.active;
                  const gps = getGpsStatus(v.current_lat, v.current_lng, v.last_location_update);
                  const studentPct = v.capacity > 0 ? Math.round(((v.student_count || 0) / v.capacity) * 100) : 0;
                  return (
                    <TableRow key={v.id || i} hover sx={{ opacity: v.status === 'inactive' ? 0.65 : 1 }}>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary', width: 40 }}>{i + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 38, height: 38, background: 'linear-gradient(135deg,#1a237e,#1565c0)', flexShrink: 0 }}>
                            <DirectionsBus sx={{ fontSize: 20 }} />
                          </Avatar>
                          <Box>
                            <Typography fontWeight={700} fontSize={13}>{v.vehicle_name}</Typography>
                            <Typography fontSize={11} color="#1a237e" fontWeight={600}>{v.vehicle_number}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Box>
                            <Typography fontSize={13} fontWeight={600}>{v.driver_name}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Phone sx={{ fontSize: 12, color: 'text.secondary' }} />
                              <Typography fontSize={11} color="text.secondary">{v.driver_phone}</Typography>
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: 130 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Group sx={{ fontSize: 14, color: '#1a237e' }} />
                          <Typography fontSize={12} fontWeight={600} color="#1a237e">
                            {v.student_count || 0} / {v.capacity}
                          </Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={Math.min(studentPct, 100)}
                          sx={{ height: 5, borderRadius: 3, background: '#e8eaf6', width: 100,
                            '& .MuiLinearProgress-bar': { background: studentPct > 90 ? '#c62828' : 'linear-gradient(90deg,#1a237e,#1565c0)', borderRadius: 3 } }} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1.5, background: gps === 'active' ? '#e8f5e9' : '#fff3e0', width: 'fit-content' }}>
                          {gps === 'active'
                            ? <GpsFixed sx={{ fontSize: 14, color: '#2e7d32' }} />
                            : <GpsOff sx={{ fontSize: 14, color: '#e65100' }} />}
                          <Typography fontSize={11} fontWeight={600} color={gps === 'active' ? '#2e7d32' : '#e65100'}>
                            {gps === 'active' ? 'Active' : 'Offline'}
                          </Typography>
                        </Box>
                        {v.last_location_update && (
                          <Typography fontSize={10} color="text.disabled" mt={0.3}>
                            {dayjs(v.last_location_update).format('DD MMM HH:mm')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={v.status || 'active'} size="small"
                          sx={{ background: sc.bg, color: sc.color, fontWeight: 700, fontSize: 10, textTransform: 'capitalize' }} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                          <Tooltip title="Manage Students">
                            <Button size="small" variant="contained" startIcon={<GroupAdd sx={{ fontSize: 14 }} />}
                              onClick={() => openStudents(v)}
                              sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 1.5, fontWeight: 700, fontSize: 11, px: 1.2, minWidth: 0 }}>
                              Students
                            </Button>
                          </Tooltip>
                          <Tooltip title="Track Vehicle">
                            <Button size="small" variant="contained" startIcon={<LocationOn sx={{ fontSize: 14 }} />}
                              onClick={() => openTracking(v)}
                              sx={{ background: 'linear-gradient(135deg,#2e7d32,#388e3c)', borderRadius: 1.5, fontWeight: 700, fontSize: 11, px: 1.2, minWidth: 0 }}>
                              Track
                            </Button>
                          </Tooltip>
                          <Tooltip title="Monthly Summary">
                            <Button size="small" variant="contained" startIcon={<Assessment sx={{ fontSize: 14 }} />}
                              onClick={() => openSummary(v)}
                              sx={{ background: 'linear-gradient(135deg,#6a1b9a,#8e24aa)', borderRadius: 1.5, fontWeight: 700, fontSize: 11, px: 1.2, minWidth: 0 }}>
                              Summary
                            </Button>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>Add New Vehicle</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField fullWidth size="small" label="Vehicle Number *" value={form.vehicle_number}
              onChange={e => setForm(p => ({ ...p, vehicle_number: e.target.value.toUpperCase() }))} placeholder="e.g. KA01AB1234" />
            <TextField fullWidth size="small" label="Vehicle Name *" value={form.vehicle_name} onChange={f('vehicle_name')} placeholder="e.g. School Van 1" />
            <TextField fullWidth size="small" label="Driver Name *" value={form.driver_name} onChange={f('driver_name')} />
            <TextField fullWidth size="small" label="Driver Phone * (10 digits)" value={form.driver_phone}
              onChange={e => setForm(p => ({ ...p, driver_phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} inputProps={{ maxLength: 10 }} />
            <TextField fullWidth size="small" label="Driver License" value={form.driver_license} onChange={f('driver_license')} />
            <TextField fullWidth size="small" label="Capacity" type="number" value={form.capacity} onChange={f('capacity')} />
            <TextField fullWidth size="small" label="GPS Device ID" value={form.gps_device_id} onChange={f('gps_device_id')}
              placeholder="IMEI or device ID" sx={{ gridColumn: '1 / -1' }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setAddDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleAdd} variant="contained" disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Add Vehicle'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={studentsDialog} onClose={() => setStudentsDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography fontWeight={700} color="#1a237e">Manage Students — {selectedVehicle?.vehicle_name}</Typography>
              <Typography fontSize={12} color="text.secondary">{selectedIds.length} / {selectedVehicle?.capacity} assigned</Typography>
            </Box>
            <Chip label={selectedIds.length + ' selected'} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700 }} />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
            <TextField size="small" fullWidth placeholder="Search student..." value={studentSearch}
              onChange={e => setStudentSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
          </Box>
          {studentsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
          ) : (
            <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
              {assignedStudents.length > 0 && (
                <Box>
                  <Box sx={{ px: 2, py: 1, background: '#e8f5e9' }}>
                    <Typography fontSize={12} fontWeight={700} color="#2e7d32">Assigned ({assignedStudents.length})</Typography>
                  </Box>
                  {assignedStudents.map(s => (
                    <Box key={s.ID} onClick={() => toggleStudent(s.ID)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.2, cursor: 'pointer', borderBottom: '1px solid #f5f5f5', background: selectedIds.includes(s.ID) ? '#e8f5e9' : '#fff', '&:hover': { background: '#f5f5f5' } }}>
                      {selectedIds.includes(s.ID) ? <CheckBox sx={{ color: '#2e7d32', fontSize: 20 }} /> : <CheckBoxOutlineBlank sx={{ color: '#ccc', fontSize: 20 }} />}
                      <Avatar src={s.profile_pic} sx={{ width: 30, height: 30, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontSize: 12 }}>{s.full_name?.[0]}</Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography fontSize={13} fontWeight={600}>{s.full_name}</Typography>
                        <Typography fontSize={11} color="text.secondary">{s.standard} - {s.section} · Roll: {s.roll_number || '—'}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              {unassignedStudents.length > 0 && (
                <Box>
                  <Box sx={{ px: 2, py: 1, background: '#f5f6fa' }}>
                    <Typography fontSize={12} fontWeight={700} color="text.secondary">Available ({unassignedStudents.length})</Typography>
                  </Box>
                  {unassignedStudents.map(s => (
                    <Box key={s.ID} onClick={() => toggleStudent(s.ID)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.2, cursor: 'pointer', borderBottom: '1px solid #f5f5f5', background: selectedIds.includes(s.ID) ? '#e8eaf6' : '#fff', '&:hover': { background: '#f5f5f5' } }}>
                      {selectedIds.includes(s.ID) ? <CheckBox sx={{ color: '#1a237e', fontSize: 20 }} /> : <CheckBoxOutlineBlank sx={{ color: '#ccc', fontSize: 20 }} />}
                      <Avatar src={s.profile_pic} sx={{ width: 30, height: 30, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontSize: 12 }}>{s.full_name?.[0]}</Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography fontSize={13} fontWeight={600}>{s.full_name}</Typography>
                        <Typography fontSize={11} color="text.secondary">{s.standard} - {s.section} · Roll: {s.roll_number || '—'}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setStudentsDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleAssign} variant="contained" disabled={assigning}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            {assigning ? <CircularProgress size={20} color="inherit" /> : 'Save (' + selectedIds.length + ' students)'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={trackDialog} onClose={closeTracking} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOn sx={{ color: '#2e7d32' }} />
            Track — {selectedVehicle?.vehicle_name}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {trackLoading && !location ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
          ) : location?.latitude ? (
            <Box>
              <Box sx={{ p: 2, background: '#e8f5e9', borderRadius: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <GpsFixed sx={{ color: '#2e7d32' }} />
                  <Typography fontWeight={700} color="#2e7d32">GPS Active</Typography>
                  {trackLoading && <CircularProgress size={14} sx={{ color: '#2e7d32', ml: 1 }} />}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Box>
                    <Typography fontSize={11} color="text.secondary">Latitude</Typography>
                    <Typography fontSize={13} fontWeight={600}>{parseFloat(location.latitude).toFixed(6)}</Typography>
                  </Box>
                  <Box>
                    <Typography fontSize={11} color="text.secondary">Longitude</Typography>
                    <Typography fontSize={13} fontWeight={600}>{parseFloat(location.longitude).toFixed(6)}</Typography>
                  </Box>
                  {location.speed !== undefined && location.speed !== null && (
                    <Box>
                      <Typography fontSize={11} color="text.secondary">Speed</Typography>
                      <Typography fontSize={13} fontWeight={600}>{parseFloat(location.speed || 0).toFixed(1)} km/h</Typography>
                    </Box>
                  )}
                  {location.timestamp && (
                    <Box>
                      <Typography fontSize={11} color="text.secondary">Last Update</Typography>
                      <Typography fontSize={13} fontWeight={600}>{dayjs(location.timestamp).format('hh:mm:ss A')}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              <Box sx={{ borderRadius: 2, overflow: 'hidden', mb: 2, border: '1px solid #e8eaf6' }}>
                <iframe title="vehicle-location" width="100%" height="260" style={{ border: 'none', display: 'block' }}
                  src={`https://maps.google.com/maps?q=${location.latitude},${location.longitude}&z=15&output=embed`} />
              </Box>
              <Button fullWidth variant="contained" startIcon={<LocationOn />}
                href={`https://www.google.com/maps?q=${location.latitude},${location.longitude}`}
                target="_blank"
                sx={{ background: 'linear-gradient(135deg,#2e7d32,#388e3c)', borderRadius: 2, fontWeight: 700, mb: 1 }}>
                Open in Google Maps
              </Button>
              <Typography fontSize={11} color="text.secondary" textAlign="center">Auto-refreshes every 10 seconds</Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <GpsOff sx={{ fontSize: 52, color: '#e8eaf6', mb: 1 }} />
              <Typography color="text.secondary" fontWeight={500}>No GPS data available</Typography>
              <Typography variant="caption" color="text.secondary">GPS device may be offline or not transmitting</Typography>
              <Box mt={2}>
                <Button variant="outlined" onClick={() => fetchLocation(selectedVehicle?.id)}
                  sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e' }}>Retry</Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={closeTracking} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Monthly Summary Dialog ── */}
      <Dialog open={summaryDialog} onClose={() => setSummaryDialog(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#6a1b9a', borderBottom: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assessment sx={{ color: '#6a1b9a' }} />
              <Box>
                <Typography fontWeight={700} color="#6a1b9a">Monthly Summary — {summaryVehicle?.vehicle_name}</Typography>
                <Typography fontSize={12} color="text.secondary">{summaryVehicle?.vehicle_number}</Typography>
              </Box>
            </Box>
            <TextField
              type="month"
              size="small"
              value={summaryMonth}
              onChange={e => handleSummaryMonthChange(e.target.value)}
              inputProps={{ max: dayjs().format('YYYY-MM') }}
              sx={{ minWidth: 160 }}
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {summaryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#6a1b9a' }} /></Box>
          ) : !summaryData ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Assessment sx={{ fontSize: 52, color: '#e8eaf6', mb: 1 }} />
              <Typography color="text.secondary" fontWeight={500}>
                {summaryError || 'No data available'}
              </Typography>
              {summaryError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, px: 2 }}>
                  {summaryError}
                </Typography>
              )}
              <Button variant="outlined" size="small" sx={{ mt: 2, borderRadius: 2, color: '#6a1b9a', borderColor: '#6a1b9a' }}
                onClick={() => fetchSummary(summaryVehicle?.id, summaryMonth)}>
                Retry
              </Button>
            </Box>
          ) : !summaryData.daily_breakdown?.length ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Timeline sx={{ fontSize: 52, color: '#e8eaf6', mb: 1 }} />
              <Typography color="text.secondary" fontWeight={500}>
                No travel data for {summaryData.period?.month_name} {summaryData.period?.year}
              </Typography>
              <Typography variant="caption" color="text.secondary">GPS data will appear here once the vehicle starts transmitting</Typography>
            </Box>
          ) : (
            <Box>
              {/* Monthly Total KMs */}
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, background: 'linear-gradient(135deg,#f3e5f5,#e8eaf6)', mb: 3, textAlign: 'center', border: '1px solid #ce93d8' }}>
                <Typography fontSize={12} fontWeight={700} color="#6a1b9a" textTransform="uppercase" letterSpacing={1} mb={0.5}>
                  {summaryData.period?.month_name} {summaryData.period?.year} — Total Distance
                </Typography>
                <Typography fontSize={52} fontWeight={900} color="#6a1b9a" lineHeight={1}>
                  {summaryData.total_km}
                </Typography>
                <Typography fontSize={16} fontWeight={700} color="#6a1b9a">km</Typography>
                <Chip label={`${summaryData.active_days} active days`} size="small"
                  sx={{ mt: 1, background: '#ce93d8', color: '#fff', fontWeight: 700, fontSize: 11 }} />
              </Paper>

              {/* Daily Breakdown */}
              <Typography fontWeight={700} fontSize={13} color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Daily Distance
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: '#f5f6fa' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#6a1b9a' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#6a1b9a' }}>Distance</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#6a1b9a' }}>Track Path</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summaryData.daily_breakdown.map((day, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>
                          {dayjs(day.date).format('DD MMM YYYY')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${day.distance_km} km`}
                            size="small"
                            sx={{
                              background: day.distance_km > 0 ? '#e8eaf6' : '#f5f5f5',
                              color: day.distance_km > 0 ? '#1a237e' : '#999',
                              fontWeight: 700, fontSize: 12
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View GPS path on map">
                            <Button size="small" variant="contained" startIcon={<MapIcon sx={{ fontSize: 13 }} />}
                              onClick={() => openPathDialog(day)}
                              sx={{ background: 'linear-gradient(135deg,#0277bd,#0288d1)', borderRadius: 1.5, fontWeight: 700, fontSize: 10, px: 1, minWidth: 0 }}>
                              Track Path
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setSummaryDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Track Path Dialog ── */}
      <Dialog open={pathDialog} onClose={closePathDialog} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#0277bd', borderBottom: '1px solid #f0f0f0', pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={closePathDialog} sx={{ mr: 0.5 }}>
              <ArrowBack fontSize="small" />
            </IconButton>
            <MapIcon sx={{ color: '#0277bd' }} />
            <Box>
              <Typography fontWeight={700} color="#0277bd">
                GPS Track Path — {summaryVehicle?.vehicle_name}
              </Typography>
              <Typography fontSize={12} color="text.secondary">
                {pathDate ? dayjs(pathDate).format('dddd, DD MMMM YYYY') : ''}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {pathLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, gap: 2 }}>
              <CircularProgress sx={{ color: '#0277bd' }} />
              <Typography color="text.secondary" fontSize={13}>Loading GPS path data…</Typography>
            </Box>
          ) : pathError ? (
            <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
              <Route sx={{ fontSize: 56, color: '#e8eaf6', mb: 1.5 }} />
              <Typography color="text.secondary" fontWeight={600} fontSize={15}>No path data available</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {pathError}
              </Typography>
            </Box>
          ) : pathPoints.length > 0 ? (
            <Box>
              {/* Stats bar */}
              <Box sx={{ display: 'flex', gap: 2, px: 2.5, py: 1.5, background: '#e3f2fd', borderBottom: '1px solid #bbdefb', flexWrap: 'wrap', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#2e7d32', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                  <Typography fontSize={12} fontWeight={600} color="#2e7d32">Start: {dayjs(String(pathPoints[0]?.timestamp).replace(' ', 'T')).format('hh:mm:ss A')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#c62828', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                  <Typography fontSize={12} fontWeight={600} color="#c62828">End: {dayjs(String(pathPoints[pathPoints.length - 1]?.timestamp).replace(' ', 'T')).format('hh:mm:ss A')}</Typography>
                </Box>
                {/* <Chip label={`${pathPoints.length} GPS points`} size="small"
                  sx={{ background: '#bbdefb', color: '#0277bd', fontWeight: 700, fontSize: 11 }} /> */}

                {/* Map layer switcher */}
                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                  {Object.entries(MAP_LAYERS).map(([key, val]) => (
                    <Button
                      key={key}
                      size="small"
                      variant={mapLayer === key ? 'contained' : 'outlined'}
                      onClick={() => setMapLayer(key)}
                      sx={{
                        fontSize: 10, fontWeight: 700, px: 1, py: 0.3, minWidth: 0, borderRadius: 1.5,
                        ...(mapLayer === key
                          ? { background: '#0277bd', color: '#fff', borderColor: '#0277bd' }
                          : { color: '#0277bd', borderColor: '#90caf9', background: '#fff' }),
                      }}
                    >
                      {val.label}
                    </Button>
                  ))}
                </Box>
              </Box>

              {/* Leaflet map container */}
              <Box
                ref={mapRef}
                sx={{ width: '100%', height: 460 }}
              />
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={closePathDialog} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
